'use client'

import { FormEvent, useState } from 'react'
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { useAuth } from '@/Auth/useAuth'
import { useSearchParams } from 'next/navigation'
import { useRouter } from 'next/navigation'

type SearchBoxInnerProps = {
  initialQuery: string
  initialPurchased: boolean
}

const SearchBoxInner = ({
  initialQuery,
  initialPurchased,
}: SearchBoxInnerProps) => {
  const router = useRouter()
  const searchParams = useSearchParams()
  const [searchTerm, setSearchTerm] = useState(initialQuery)
  const [showPurchasedOnly, setShowPurchasedOnly] = useState(initialPurchased)
  const { isAuthenticated } = useAuth()

  const updateSearchTerm = (value: string) => {
    setSearchTerm(value)
  }

  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const urlQuery = searchParams.get('query') || ''
    const trimmedSearchTerm = searchTerm.trim()
    const params = new URLSearchParams(searchParams.toString())

    if (trimmedSearchTerm !== urlQuery) {
      params.delete('book')
    }

    if (trimmedSearchTerm && trimmedSearchTerm.length >= 2) {
      params.set('query', trimmedSearchTerm)
    } else {
      params.delete('query')
    }
    if (showPurchasedOnly) {
      params.set('purchased', 'true')
    } else {
      params.delete('purchased')
    }
    params.set('page', '1')

    router.replace(`/discoverie?${params.toString()}`)
  }

  return (
    <div className="space-y-2">
      <form className="flex flex-col gap-3 sm:flex-row" onSubmit={submitSearch}>
        <div className="relative min-w-0 flex-1">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4">
            <MagnifyingGlassIcon
              aria-hidden="true"
              className="h-5 w-5 text-gray-400"
            />
          </div>
          <input
            value={searchTerm}
            onChange={e => updateSearchTerm(e.target.value)}
            id="searchTerm"
            type="text"
            placeholder="Search for tricks, moves, magicians, or authors..."
            className="block w-full rounded-lg border-0 bg-gray-50 py-3 pl-12 pr-12 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:bg-white focus:ring-2 focus:ring-inset focus:ring-vi-red sm:text-lg transition-all duration-200"
          />
          {searchTerm ? (
            <button
              type="button"
              onClick={() => {
                setSearchTerm('')
              }}
              className="absolute inset-y-0 right-0 flex items-center pr-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <XMarkIcon className="h-5 w-5" aria-hidden="true" />
              <span className="sr-only">Clear search</span>
            </button>
          ) : null}
        </div>
        <div>
          <button
            type="submit"
            className="inline-flex h-full min-h-12 w-full items-center justify-center rounded-lg bg-vi-red px-5 text-sm font-semibold text-white transition hover:bg-red-800 focus:outline-none focus:ring-2 focus:ring-vi-red focus:ring-offset-2 sm:w-auto"
          >
            Search
          </button>
        </div>
      </form>
      <p className="text-xs leading-5 text-gray-500">
        Tips: use <span className="font-mono text-gray-700">@title: Royal</span>{' '}
        or <span className="font-mono text-gray-700">@author: Hugard</span> to
        narrow books. Use quotes for exact phrases, like{' '}
        <span className="font-mono text-gray-700">&quot;double lift&quot;</span>
        .
      </p>
      {isAuthenticated && (
        <div className="flex items-center">
          <input
            type="checkbox"
            id="showPurchasedOnly"
            checked={showPurchasedOnly}
            onChange={e => setShowPurchasedOnly(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-vi-red focus:ring-vi-red"
          />
          <label
            htmlFor="showPurchasedOnly"
            className="ml-2 text-sm text-gray-600"
          >
            Show results from my purchases only
          </label>
        </div>
      )}
    </div>
  )
}

export const SearchBox = () => {
  const searchParams = useSearchParams()
  const initialQuery = searchParams.get('query') || ''
  const initialPurchased = searchParams.get('purchased') === 'true'
  const syncKey = searchParams.toString()

  return (
    <SearchBoxInner
      key={syncKey}
      initialQuery={initialQuery}
      initialPurchased={initialPurchased}
    />
  )
}
